import { EditorContent } from "@tiptap/react"
import { useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Node, mergeAttributes } from "@tiptap/core"
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react"
import React, { useState } from "react"
import { Button } from "@/components/ui/Button"
import { Dialog, DialogPopup, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog"
import { Input } from "@/components/ui/Input"

// Variable Edit Dialog Component
const VariableEditDialog = ({ isOpen, onClose, variableName, currentValue, onSave }: {
  isOpen: boolean
  onClose: () => void
  variableName: string
  currentValue: string
  onSave: (value: string) => void
}) => {
  const [inputValue, setInputValue] = useState('')

  // Reset input value when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setInputValue(currentValue || '')
    }
  }, [isOpen, currentValue])

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSave(inputValue)
    onClose()
  }

  const handleClose = () => {
    setInputValue('') // Reset on close
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Edit Variable: {variableName}</DialogTitle>
        </DialogHeader>
        <form className="py-4 space-y-4" onSubmit={handleSave}>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter variable value"
          />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit">
            Save
          </Button>
        </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  )
}

// Variable Name Dialog Component
const VariableNameDialog = ({ isOpen, onClose, currentName, onSave }: {
  isOpen: boolean
  onClose: () => void
  currentName: string
  onSave: (name: string) => void
}) => {
  const [inputValue, setInputValue] = useState('')

  React.useEffect(() => {
    if (isOpen) {
      setInputValue(currentName || '')
    }
  }, [isOpen, currentName])

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (inputValue.trim()) {
      onSave(inputValue.trim())
      onClose()
    }
  }

  const handleClose = () => {
    setInputValue('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Edit Variable Name</DialogTitle>
        </DialogHeader>
        <form className="py-4 space-y-4" onSubmit={handleSave}>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter variable name"/>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit">
            Save
          </Button>
        </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  )
}

// Variable Node Component
const VariableComponent = ({ node, updateAttributes, deleteNode, extension }: any) => {
  const variableName = node.attrs.name;
  const onVariableChange = extension.options.onVariableChange;
  const variables = extension.options.variables;
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  
  return (
    <NodeViewWrapper className="inline-block">
      <span
        className="bg-indigo-500 text-indigo-50 px-[1px] py-[1px] mr-1 rounded-md border border-indigo-500/30 cursor-pointer hover:bg-indigo-500/30 transition-colors"
        onClick={() => {
          if (onVariableChange) {
            setEditDialogOpen(true)
          } else {
            setNameDialogOpen(true)
          }
        }}
        onDoubleClick={deleteNode}
      >
        {`{${variableName}}`}
      </span>
      
      <VariableEditDialog
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        variableName={variableName}
        currentValue={variables && variables[variableName] ? variables[variableName] : ''}
        onSave={(value) => onVariableChange?.(variableName, value)}
      />
      
      <VariableNameDialog
        isOpen={nameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        currentName={node.attrs.name}
        onSave={(name) => updateAttributes({ name })}
      />
    </NodeViewWrapper>
  )
}

// Variable Node Extension
const VariableNode = Node.create({
  name: "variable",
  
  group: "inline",
  
  inline: true,
  
  atom: true,
  
  addOptions() {
    return {
      onVariableChange: null,
      variables: null,
    }
  },
  
  addAttributes() {
    return {
      name: {
        default: "variable",
        parseHTML: element => element.getAttribute("data-name"),
        renderHTML: attributes => {
          return {
            "data-name": attributes.name,
          }
        },
      },
    }
  },
  
  parseHTML() {
    return [
      {
        tag: "span[data-type=\"variable\"]",
      },
    ]
  },
  
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes({ "data-type": "variable" }, HTMLAttributes), HTMLAttributes["data-name"]]
  },
  
  addNodeView() {
    return ReactNodeViewRenderer(VariableComponent)
  },
})


export const RichTextEditor = ({ content, templateParts, variables, onVariableChange }: { 
  content: string,
  templateParts?: Array<string | { var: string }>,
  variables?: Record<string, string>,
  onVariableChange?: (key: string, value: string) => void
}) => {
  const [insertDialogOpen, setInsertDialogOpen] = useState(false)
  
  // Convert template to rich content with variable nodes
  const convertToRichContent = () => {
    if (!templateParts || !variables) return content;
    
    const nodes = templateParts.map((part, idx) => {
      if (typeof part === 'string') {
        return { type: 'text', text: part };
      } else {
        return {
          type: 'variable',
          attrs: { name: part.var }
        };
      }
    });
    
    return {
      type: 'doc',
      content: [{
        type: 'paragraph',
        content: nodes
      }]
    };
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      VariableNode.configure({
        onVariableChange: onVariableChange,
        variables: variables
      }),
    ],
    content: templateParts ? convertToRichContent() : content,
  })

  // Update editor when variables change
  React.useEffect(() => {
    if (editor && templateParts && variables) {
      // Force re-render of variable nodes by updating their options
      const variableExtension = editor.extensionManager.extensions.find(ext => ext.name === 'variable');
      if (variableExtension) {
        variableExtension.options.variables = variables;
        editor.view.updateState(editor.state);
      }
    }
  }, [variables, editor])

  const insertVariable = () => {
    setInsertDialogOpen(true)
  }

  const handleInsertVariable = (name: string) => {
    if (name && editor) {
      editor.chain().focus().insertContent({
        type: 'variable',
        attrs: { name }
      }).run()
      
      // Also add the variable to the variables state if onVariableChange exists
      if (onVariableChange && (!variables || !variables[name])) {
        onVariableChange(name, '')
      }
    }
  }

  return (
    <div>
      <div className="mb-2">
        <Button
          className="mb-4"
          onClick={insertVariable}
        >
          Insert Variable
        </Button>
      </div>
      <EditorContent editor={editor} className="text-white" />
      
      <VariableNameDialog
        isOpen={insertDialogOpen}
        onClose={() => setInsertDialogOpen(false)}
        currentName=""
        onSave={handleInsertVariable}
      />
    </div>
  )
}